import { graphlib, layout } from "@dagrejs/dagre";
import { Edge as FlowEdge, Node as FlowNode } from "@xyflow/react";

const DEFAULT_NODE_HEIGHT = 105;

const nodeConfigMap: Record<string, { height: number }> = {
  trigger: { height: 100 },
  sendMessage: { height: 300 },
  recover: { height: 400 },
};

// Must match NodeTypes.CHECKOUT_EXPERIENCE in the advanced flow editor.
const CHECKOUT_EXPERIENCE_NODE_TYPE = "checkout_experience";

// Checkout-experience nodes don't have a single fixed height: the card body
// grows with the category summary chips (shipping / widgets / post-purchase),
// and the readonly tree view stacks a dark metrics callout beneath the card.
// Estimate the rendered height from the node's data so dagre reserves enough
// vertical room and the next rank doesn't overlap the metrics callout.
//
// Card chrome + header row + the always-present "Name" badge section.
const CHECKOUT_EXPERIENCE_BASE_HEIGHT = 112;
// Category summary chips row (shipping / widgets / post-purchase).
const CHECKOUT_EXPERIENCE_SUMMARY_HEIGHT = 58;
// Dark metrics callout stacked beneath the card in the readonly tree view.
const CHECKOUT_EXPERIENCE_METRICS_HEIGHT = 76;

function asRecord(value: unknown): Record<string, unknown> {
  return typeof value === "object" && value !== null
    ? // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      (value as Record<string, unknown>)
    : {};
}

export function getCheckoutExperienceNodeHeight(data: unknown): number {
  const isReadonly = asRecord(data).readonly === true;
  return (
    CHECKOUT_EXPERIENCE_BASE_HEIGHT +
    CHECKOUT_EXPERIENCE_SUMMARY_HEIGHT +
    (isReadonly ? CHECKOUT_EXPERIENCE_METRICS_HEIGHT : 0)
  );
}

// Must match NodeTypes.HSA_* in the advanced flow editor.
const HSA_QUESTION_GROUP_NODE_TYPE = "hsa_question_group";
const HSA_CONDITION_NODE_TYPE = "hsa_condition";
// The HSA nodes abbreviate their lists to this many rows plus a "+N more"
// pill; the node components import this so rendering and layout agree.
export const HSA_MAX_LIST_ROWS = 3;

// Card chrome + header row + the "N questions" count line.
const HSA_GROUP_BASE_HEIGHT = 108;
const HSA_GROUP_QUESTION_ROW_HEIGHT = 24;
// Card chrome + header row.
const HSA_CONDITION_BASE_HEIGHT = 100;
// Connector + question label + "Matching answer" rows per leaf.
const HSA_CONDITION_LEAF_HEIGHT = 62;
const HSA_OVERFLOW_PILL_HEIGHT = 28;

function hsaListLength(data: unknown, key: string): number {
  const list = asRecord(asRecord(data).step)[key];
  return Array.isArray(list) ? list.length : 0;
}

export function getHsaQuestionGroupNodeHeight(data: unknown): number {
  const questions = hsaListLength(data, "questions");
  const shown = Math.min(questions, HSA_MAX_LIST_ROWS);
  return (
    HSA_GROUP_BASE_HEIGHT +
    shown * HSA_GROUP_QUESTION_ROW_HEIGHT +
    (questions > shown ? HSA_OVERFLOW_PILL_HEIGHT : 0)
  );
}

export function getHsaConditionNodeHeight(data: unknown): number {
  const condition = asRecord(asRecord(asRecord(data).step).condition);
  const blocks = Array.isArray(condition.conditionBlocks)
    ? condition.conditionBlocks
    : [];
  // Count only configured leaves — the node renders nothing for a leaf
  // whose question hasn't been picked yet.
  const leaves = blocks.reduce((total: number, block: unknown) => {
    const conditions = asRecord(block).conditions;
    if (!Array.isArray(conditions)) {
      return total;
    }
    return (
      total +
      conditions.filter((leaf) => !!asRecord(leaf).questionHandle).length
    );
  }, 0);
  const shown = Math.max(Math.min(leaves, HSA_MAX_LIST_ROWS), 1);
  return (
    HSA_CONDITION_BASE_HEIGHT +
    shown * HSA_CONDITION_LEAF_HEIGHT +
    (leaves > shown ? HSA_OVERFLOW_PILL_HEIGHT : 0)
  );
}

export function getNodeHeight(node: FlowNode): number {
  if (node.type === CHECKOUT_EXPERIENCE_NODE_TYPE) {
    return getCheckoutExperienceNodeHeight(node.data);
  }
  if (node.type === HSA_QUESTION_GROUP_NODE_TYPE) {
    return getHsaQuestionGroupNodeHeight(node.data);
  }
  if (node.type === HSA_CONDITION_NODE_TYPE) {
    return getHsaConditionNodeHeight(node.data);
  }
  return nodeConfigMap[node.type ?? ""]?.height ?? DEFAULT_NODE_HEIGHT;
}

export const getLayoutedElements = (nodes: FlowNode[], edges: FlowEdge[]) => {
  const g = new graphlib.Graph({}).setDefaultEdgeLabel(() => ({}));

  g.setGraph({ rankdir: "TB", ranksep: 105, nodesep: 300 });

  for (const edge of edges) {
    g.setEdge(edge.source, edge.target);
  }

  for (const node of nodes) {
    g.setNode(node.id, { width: 0, height: getNodeHeight(node) });
  }

  layout(g);

  const layoutedNodes = nodes.map((node: any) => {
    const offset = getNodeHeight(node) / 2;

    const position = g.node(node.id);
    const x = position.x;
    const y = position.y - offset;

    return { ...node, position: { x, y } };
  });

  return { nodes: layoutedNodes, edges };
};
